import { useEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren, JSX } from "preact";

/**
 * A small dropdown: opens from a button, closes on Escape, on a click outside,
 * or when one of its items runs. Focus returns to the button on Escape so a
 * keyboard user is not dropped at the top of the page.
 */
export function Menu({
  label,
  button,
  buttonClass = "btn sm",
  wrapClass = "",
  children,
}: {
  label: string;
  /** Extra class on the wrapper, e.g. to hide the menu on small screens. */
  wrapClass?: string;
  button: ComponentChildren;
  buttonClass?: string;
  children: (close: () => void) => ComponentChildren;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent): void => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div class={`menu-wrap ${wrapClass}`.trim()} ref={wrap}>
      <button
        ref={trigger}
        class={buttonClass}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        {button}
      </button>
      {open ? (
        <div class="menu" role="menu">
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}
